/**
 * Usage history / billing: functionality type filter options and display helpers.
 * Keep in sync with GET /usage/history and /admin/usage/history `functionality` param.
 */
export const USAGE_TYPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All types' },
  { value: 'optimization', label: 'Optimization' },
  { value: 'evaluation', label: 'Evaluation' },
  { value: 'fine_tuning', label: 'Fine Tuning' },
  { value: 'dataset-generation', label: 'Dataset Generation' },
  { value: 'chat', label: 'Chat' },
  { value: 'rag', label: 'RAG' },
  { value: 'nlt', label: 'NLT' },
  { value: 'leaderboard', label: 'Leaderboard' },
  { value: 'other', label: 'Other' },
];

export const USAGE_FUNCTIONALITY_BADGE_STYLES: Record<string, { bg: string; color: string; icon: string }> = {
  optimization: { bg: '#FFFFFF', color: '#0099FA', icon: 'fa-chart-line' },
  evaluation: { bg: '#FFFFFF', color: '#0072C6', icon: 'fa-clipboard-check' },
  fine_tuning: { bg: '#F5F9FE', color: '#B85400', icon: 'fa-graduation-cap' },
  'dataset-generation': { bg: '#FFFFFF', color: '#0072C6', icon: 'fa-database' },
  chat: { bg: '#FFFFFF', color: '#0099FA', icon: 'fa-comment' },
  rag: { bg: '#F5F9FE', color: '#0072C6', icon: 'fa-book' },
  nlt: { bg: '#FFFFFF', color: '#0099FA', icon: 'fa-wand-magic-sparkles' },
  leaderboard: { bg: '#F5F9FE', color: '#B85400', icon: 'fa-trophy' },
  /** Unknown / fallback — use a neutral icon (not ellipsis) so it is not confused with “…” */
  other: { bg: '#F5F9FE', color: '#5B7893', icon: 'fa-tag' },
};

const LABEL_BY_KEY: Record<string, string> = {
  optimization: 'Optimization',
  evaluation: 'Evaluation',
  fine_tuning: 'Fine Tuning',
  'dataset-generation': 'Dataset Gen',
  chat: 'Chat',
  rag: 'RAG',
  nlt: 'NLT',
  leaderboard: 'Leaderboard',
  other: 'Other',
};

export function normalizeUsageFunctionalityKey(value: string | undefined | null): string {
  const raw = (value ?? '').trim();
  return raw ? raw.toLowerCase() : 'other';
}

/** Resolve functionality for list/detail API and badges (handles legacy `other` + entity_id `nlt`). */
export function resolveUsageFunctionalityKey(
  functionality: string | undefined | null,
  entityId?: string | null
): string {
  const funcKey = normalizeUsageFunctionalityKey(functionality);
  const entityKey = normalizeUsageFunctionalityKey(entityId);
  if (funcKey === 'nlt' || entityKey === 'nlt') return 'nlt';
  return funcKey;
}

export function formatUsageFunctionalityLabel(value: string | undefined | null): string {
  const key = normalizeUsageFunctionalityKey(value);
  if (LABEL_BY_KEY[key]) return LABEL_BY_KEY[key];
  const raw = (value ?? '').trim();
  if (!raw) return 'Other';
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getUsageFunctionalityBadgeStyle(value: string | undefined | null) {
  const key = normalizeUsageFunctionalityKey(value);
  return USAGE_FUNCTIONALITY_BADGE_STYLES[key] ?? USAGE_FUNCTIONALITY_BADGE_STYLES.other;
}
